import React, { useState } from "react";
import { MapPin, Phone, Clock, Mail, CheckCircle } from "lucide-react";
import "./PageStyles.css";
import "./ContactPage.css";

const SERVICES = [
  "Window Repair",
  "Screen Repair",
  "Glass Repair",
  "Key Cutting",
  "Paint Matching",
  "Tools & Supplies",
  "General Question",
  "Other",
];

function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email))
      e.email = "A valid email is required.";
    if (!form.message.trim()) e.message = "Please tell us how we can help.";
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length > 0) {
      setErrors(e2);
      return;
    }
    // In production this would POST to /api/contact
    setSubmitted(true);
  };

  return (
    <div className="page">
      {/* Page hero */}
      <div className="page-hero page-hero--dark">
        <div className="container">
          <div className="page-hero__eyebrow">City Hardware — Detroit</div>
          <h1 className="page-hero__heading">Get in Touch.</h1>
          <p className="page-hero__sub">
            Stop in, call us, or send a message. We're here to help with
            repairs, supplies, and anything in between.
          </p>
        </div>
      </div>

      <section className="page-section contact-section">
        <div className="container contact-grid">
          {/* Left: Form */}
          <div className="contact-form-wrap">
            {submitted ? (
              <div className="contact-success">
                <CheckCircle size={48} className="contact-success__icon" />
                <h2 className="contact-success__heading">Message Sent.</h2>
                <p className="contact-success__body">
                  Thanks, {form.name.split(" ")[0]}. We'll get back to you
                  shortly — usually the same business day.
                </p>
                <button
                  className="btn btn--red"
                  onClick={() => {
                    setSubmitted(false);
                    setForm({
                      name: "",
                      email: "",
                      phone: "",
                      service: "",
                      message: "",
                    });
                  }}>
                  Send Another Message
                </button>
              </div>
            ) : (
              <>
                <div className="contact-form-header">
                  <div className="section-eyebrow">Send a Message</div>
                  <h2 className="section-heading">
                    We'll Get Back to You Fast.
                  </h2>
                </div>

                <form
                  className="contact-form"
                  onSubmit={handleSubmit}
                  noValidate>
                  <div className="contact-form__row">
                    <div
                      className={`contact-field${errors.name ? " contact-field--error" : ""}`}>
                      <label htmlFor="name" className="contact-field__label">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Jane Smith"
                        className="contact-field__input"
                        autoComplete="name"
                      />
                      {errors.name && (
                        <span className="contact-field__error">
                          {errors.name}
                        </span>
                      )}
                    </div>

                    <div
                      className={`contact-field${errors.email ? " contact-field--error" : ""}`}>
                      <label htmlFor="email" className="contact-field__label">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="jane@example.com"
                        className="contact-field__input"
                        autoComplete="email"
                      />
                      {errors.email && (
                        <span className="contact-field__error">
                          {errors.email}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="contact-form__row">
                    <div className="contact-field">
                      <label htmlFor="phone" className="contact-field__label">
                        Phone Number{" "}
                        <span className="contact-field__optional">
                          (optional)
                        </span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="(313) 000-0000"
                        className="contact-field__input"
                        autoComplete="tel"
                      />
                    </div>

                    <div className="contact-field">
                      <label htmlFor="service" className="contact-field__label">
                        What Can We Help With?{" "}
                        <span className="contact-field__optional">
                          (optional)
                        </span>
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={form.service}
                        onChange={handleChange}
                        className="contact-field__input contact-field__select">
                        <option value="">Select a service…</option>
                        {SERVICES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div
                    className={`contact-field${errors.message ? " contact-field--error" : ""}`}>
                    <label htmlFor="message" className="contact-field__label">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us what you need. The more detail the better — we'll follow up quickly."
                      className="contact-field__input contact-field__textarea"
                      rows={5}
                    />
                    {errors.message && (
                      <span className="contact-field__error">
                        {errors.message}
                      </span>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn btn--red contact-form__submit">
                    Send Message
                  </button>

                  <p className="contact-form__disclaimer">
                    For urgent needs, call us directly at{" "}
                    <a href="tel:+13135550100">(313) 555-0100</a>.
                  </p>
                </form>
              </>
            )}
          </div>

          {/* Right: Store info */}
          <div className="contact-info">
            <div className="contact-info__card">
              <h3 className="contact-info__heading">Store Information</h3>

              <div className="contact-info__list">
                <div className="contact-info__item">
                  <MapPin size={18} className="contact-info__icon" />
                  <div>
                    <div className="contact-info__label">Address</div>
                    <a
                      href="https://maps.google.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-info__value contact-info__value--link">
                      16325 W Warren Ave
                      <br />
                      Detroit, MI 48228
                    </a>
                  </div>
                </div>

                <div className="contact-info__item">
                  <Phone size={18} className="contact-info__icon" />
                  <div>
                    <div className="contact-info__label">Phone</div>
                    <a
                      href="tel:+13135550100"
                      className="contact-info__value contact-info__value--link">
                      (313) 555-0100
                    </a>
                  </div>
                </div>

                <div className="contact-info__item">
                  <Mail size={18} className="contact-info__icon" />
                  <div>
                    <div className="contact-info__label">Email</div>
                    <a
                      href="mailto:hello@cityhardware.com"
                      className="contact-info__value contact-info__value--link">
                      hello@cityhardware.com
                    </a>
                  </div>
                </div>

                <div className="contact-info__item">
                  <Clock size={18} className="contact-info__icon" />
                  <div>
                    <div className="contact-info__label">Store Hours</div>
                    <div className="contact-info__value">
                      Mon–Sat &nbsp; 7:00 AM – 8:00 PM
                    </div>
                    <div className="contact-info__value">
                      Sunday &nbsp;&nbsp;&nbsp; 9:00 AM – 5:00 PM
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-info__cta">
              <div className="contact-info__cta-label">Fastest Response</div>
              <a href="tel:+13135550100" className="contact-info__cta-phone">
                <Phone size={20} />
                (313) 555-0100
              </a>
              <p className="contact-info__cta-note">
                Call during store hours for same-day service and immediate
                answers.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactPage;
